import { Grid2X2, Image, Palette } from "lucide-react";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { MIN_GRID_CELL_SIZE, useGridCellWrapper } from "./GridCellWrapperContext";

export function GridCellWrapperControls() {
    const {
        inputSvg,
        cellSize,
        bufferCells,
        gridStroke,
        gridStrokeOpacity,
        markedFill,
        markedFillOpacity,
        wrapStroke,
        wrapStrokeWidth,
        wrapStrokeOpacity,
        gridCols,
        gridRows,
        markedCells,
        error,
        setCellSize,
        setBufferCells,
        setGridStroke,
        setGridStrokeOpacity,
        setMarkedFill,
        setMarkedFillOpacity,
        setWrapStroke,
        setWrapStrokeWidth,
        setWrapStrokeOpacity,
        handleSvgLoad,
        inputRef,
    } = useGridCellWrapper();

    const handleCellSizeChange = (v: string | number) => {
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(n) || n < MIN_GRID_CELL_SIZE) return;
        setCellSize(n);
    };

    const handleBufferCellsChange = (v: string | number) => {
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(n) || n < 0) return;
        setBufferCells(n);
    };

    return (
        <div className="panel-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Grid Cell Wrapper</h2>
                    <ToolDescriptionPopover dialogLabel="Grid Cell Wrapper tool description">
                        <p>
                            Load an SVG, generate a grid behind it, and color
                            cells that overlap any part of the source geometry.
                        </p>
                    </ToolDescriptionPopover>
                </div>
            </div>
            <div className="path-tool-inputs">
                <CollapsibleToolGroup title="Input SVG" icon={Image}>
                    <label className="field-label">SVG file</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="tool-file-btn"
                            onClick={() => inputRef.current?.click()}
                        >
                            Choose SVG...
                        </button>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleSvgLoad}
                        />
                    </div>
                    {inputSvg && (
                        <p className="upload-info">
                            Loaded: <strong>{inputSvg.name}</strong>
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Grid" icon={Grid2X2}>
                    <div className="field-row">
                        <label className="field-label">
                            Cell size (SVG units)
                            <DebouncedInput
                                type="number"
                                min={MIN_GRID_CELL_SIZE}
                                step={1}
                                value={cellSize}
                                onChange={handleCellSizeChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Buffer zone (cells)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={bufferCells}
                                onChange={handleBufferCellsChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Grid line color
                            <DebouncedInput
                                type="text"
                                value={gridStroke}
                                onChange={(v) => setGridStroke(String(v))}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Grid line opacity (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={gridStrokeOpacity}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (!Number.isFinite(n)) return;
                                    setGridStrokeOpacity(n);
                                }}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Marked cells" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Mark color
                            <DebouncedInput
                                type="text"
                                value={markedFill}
                                onChange={(v) => setMarkedFill(String(v))}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Mark opacity (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={markedFillOpacity}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (!Number.isFinite(n)) return;
                                    setMarkedFillOpacity(n);
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Wrap stroke color (outer boundary only)
                            <DebouncedInput
                                type="text"
                                value={wrapStroke}
                                onChange={(v) => setWrapStroke(String(v))}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Wrap stroke width (0 = off)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={64}
                                step={0.5}
                                value={wrapStrokeWidth}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (!Number.isFinite(n) || n < 0) return;
                                    setWrapStrokeWidth(n);
                                }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Wrap stroke opacity (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={wrapStrokeOpacity}
                                onChange={(v) => {
                                    const n =
                                        typeof v === "number" ? v : Number(v);
                                    if (!Number.isFinite(n)) return;
                                    setWrapStrokeOpacity(n);
                                }}
                            />
                        </label>
                    </div>
                    <p className="path-tool-meta">
                        Grid: {gridCols}x{gridRows} - Marked cells:{" "}
                        {markedCells.length}
                    </p>
                </CollapsibleToolGroup>
            </div>
            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
